import React from 'react';

import {EditHoc} from '../edit-hoc';
import {PartialFeature} from './group-features';
import {Markdown, MarkdownInline} from '../utils/md';

export type FeatureParentObject = {
	type: 'class' | 'specie' | 'subClass' | 'background' | 'monster' | 'classSection' | 'feat';
	parentId: string;
	supParentId?: string;
};

type Props = {
	feature: PartialFeature;
	parentObject: FeatureParentObject;
	/* @deprecated */
	lessLevel?: boolean;
	/* @deprecated */
	alwaysH1?: boolean;
	notEditable?: boolean;
	h?: number;
	/** строка под названием курсивом: так помечены особенности выбранного подкласса */
	note?: React.ReactNode;
};

export const FeatureView: React.FC<Props> = ({feature, parentObject, lessLevel, alwaysH1, notEditable, h, note}) => {
	const {type, parentId, supParentId} = parentObject;

	let headingLevel = feature.parentId ? 3 : 2;
	headingLevel = feature.headingLevel ? parseInt(feature.headingLevel, 10) : headingLevel;
	headingLevel += lessLevel ? 1 : 0;
	if (alwaysH1) headingLevel = 1;
	if (h) headingLevel = h;

	const heading = React.createElement(
		'h' + headingLevel,
		null,
		<>
			{['class', 'subClass'].includes(type) && feature.levels ? `${feature.levels.at(0)}-й уровень: ` : ''}
			{feature.title}
			{feature.prerequisite && <div className={'subTitle'}><MarkdownInline text={feature.prerequisite} /></div>}
			{note && <div className={'subTitle'}><em>{note}</em></div>}
		</>,
	);

	const content =
		// 'none' — особенность без заголовка (вводный блок, напр. «Вы получаете следующую пользу.»)
		feature.headingLevel === 'none' ? (
			<Markdown text={feature.description || ''} />
		) : [5, 6].includes(headingLevel) ? (
		<Markdown text={`*_${feature.title}._* ${feature.description}`} />
	) : (
		<>
			{heading}
			<Markdown text={feature.description || ''} />
		</>
	);

	const editable =
		notEditable || !['class', 'subClass', 'specie', 'classSection', 'feat'].includes(type) ? (
			content
		) : type === 'class' ? (
			<EditHoc
				load={() => import('../../pages/classes/edit-buttons').then((m) => m.EditClassFeatures)}
				editProps={{classId: parentId!, id: feature.id}}
			>
				{content}
			</EditHoc>
		) : type === 'subClass' ? (
			<EditHoc
				load={() => import('../../pages/classes/edit-buttons').then((m) => m.EditSubClassFeature)}
				editProps={{classId: supParentId!, subClassId: parentId!, id: feature.id}}
			>
				{content}
			</EditHoc>
		) : type === 'specie' ? (
			<EditHoc
				load={() => import('../../pages/specie/edit-buttons').then((m) => m.EditSpecieFeature)}
				editProps={{specieId: parentId, id: feature.id}}
			>
				{content}
			</EditHoc>
		) : type === 'feat' ? (
			<EditHoc
				load={() => import('../../pages/feats/edit-buttons').then((m) => m.EditFeatFeature)}
				editProps={{featId: parentId, id: feature.id}}
			>
				{content}
			</EditHoc>
		) : (
			<EditHoc
				load={() => import('../../pages/classes/edit-buttons').then((m) => m.EditClassSectionFeature)}
				editProps={{sectionType: supParentId!, id: feature.id}}
			>
				{content}
			</EditHoc>
		);

	return (
		<>
			<div id={`feature-${feature.id}`}>{editable}</div>
			{feature.features?.map((feature) => (
				<FeatureView key={feature.id} feature={feature} parentObject={parentObject} lessLevel={lessLevel} />
			))}
		</>
	);
};
